import { File as FileIcon, LoaderCircle, Paperclip, Trash2 } from 'lucide-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { api, uploadAttachment } from '../api/client';
import type { Attachment } from '../api/types';

export interface AttachmentsHandle {
  addFiles: (files: FileList | File[], rejectOnError?: boolean) => Promise<Attachment[]>;
  replaceFile: (id: number, file: File) => Promise<Attachment>;
  removeFiles: (ids: number[]) => Promise<void>;
}

export const Attachments = forwardRef<AttachmentsHandle, {
  stateId?: number;
  attachments: Attachment[];
  onChange: (attachments: Attachment[], replacedId?: number) => void;
  compact?: boolean;
  showTrigger?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
  hiddenIds?: number[];
  groups?: Record<number, number[]>;
}>(function Attachments({
  stateId,
  attachments,
  onChange,
  compact = false,
  showTrigger = true,
  onUploadingChange,
  hiddenIds = [],
  groups = {},
}, ref) {
  const input = useRef<HTMLInputElement>(null);
  const attachmentsRef = useRef(attachments);
  const pendingAdds = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  const addFiles = async (files: FileList | File[] | null, rejectOnError = false) => {
    if (!files?.length) return [];
    pendingAdds.current += 1;
    if (pendingAdds.current === 1) {
      setUploading(true);
      onUploadingChange?.(true);
    }
    setError('');
    const uploaded: Attachment[] = [];
    try {
      for (const file of Array.from(files)) {
        setProgress(0);
        uploaded.push(await uploadAttachment(file, { stateId }, setProgress, setUploadStatus));
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка загрузки');
      if (rejectOnError) throw reason;
    } finally {
      if (uploaded.length) {
        const next = [...attachmentsRef.current, ...uploaded];
        attachmentsRef.current = next;
        onChange(next);
      }
      pendingAdds.current -= 1;
      if (pendingAdds.current === 0) {
        setUploading(false);
        onUploadingChange?.(false);
      }
      if (input.current) input.current.value = '';
    }
    return uploaded;
  };

  const replaceFile = async (id: number, file: File) => {
    const original = attachmentsRef.current.find(item => item.id === id);
    if (!original) throw new Error('Эта картинка уже не в черновике. Откройте её заново.');
    pendingAdds.current += 1; setUploading(true); onUploadingChange?.(true); setError('');
    let replacement: Attachment | undefined;
    try {
      replacement = await uploadAttachment(file, { stateId }, setProgress);
      await api.deleteAttachment(original.deleteUrl);
      const next = attachmentsRef.current.map(item => item.id === id ? replacement! : item);
      attachmentsRef.current = next; onChange(next, id);
      return replacement;
    } catch (reason) {
      if (replacement) await api.deleteAttachment(replacement.deleteUrl).catch(() => undefined);
      setError('Не удалось заменить картинку. Исходный файл оставлен в черновике.');
      throw reason;
    } finally {
      pendingAdds.current -= 1;
      if (!pendingAdds.current) { setUploading(false); onUploadingChange?.(false); }
    }
  };
  const removeFiles = async (ids: number[]) => {
    for (const id of ids) {
      const attachment = attachmentsRef.current.find(item => item.id === id);
      if (!attachment) continue;
      await api.deleteAttachment(attachment.deleteUrl);
      const next = attachmentsRef.current.filter(item => item.id !== id);
      attachmentsRef.current = next; onChange(next);
    }
  };
  useImperativeHandle(ref, () => ({ addFiles: (files, rejectOnError) => addFiles(files, rejectOnError), replaceFile, removeFiles }));

  // A file can be a hundred megabytes, so report progress per uploaded chunk.
  const percent = Math.round(progress * 100);

  return (
    <div className="attachments">
      <input
        hidden
        multiple
        onChange={(event) => void addFiles(event.target.files)}
        ref={input}
        type="file"
      />
      {showTrigger ? (
        <button className={compact ? 'attachment-trigger' : 'button button--secondary'} disabled={uploading} onClick={() => input.current?.click()} type="button">
          {uploading ? <LoaderCircle className="spin" size={16} /> : <Paperclip size={16} />}
          {uploading ? uploadStatus || `Загрузка… ${percent}%` : 'Прикрепить'}
        </button>
      ) : uploading ? <span className="attachment-uploading"><LoaderCircle className="spin" size={15} /> {uploadStatus || `Загрузка… ${percent}%`}</span> : null}
      {error ? <span className="form-error">{error}</span> : null}
      {attachments.length ? (
        <div className="attachment-list">
          {attachments.filter(attachment => !hiddenIds.includes(attachment.id)).map((attachment) => (
            <span className="attachment-chip" key={attachment.id}>
              <FileIcon size={14} />
              <a href={attachment.attachmentUrl} rel="noreferrer" target="_blank">{attachment.name}</a>
              <button aria-label={`Удалить ${attachment.name}`} disabled={uploading} onClick={() => { void removeFiles([...(groups[attachment.id] || []), attachment.id]).catch(() => setError('Не удалось удалить файл. Попробуйте ещё раз.')); }} type="button">
                <Trash2 size={13} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
});
